<script setup lang="ts">
import type { Calculation } from '@/stores/type/calculation.type'
import { useCalculationStore } from '@/stores/calculation'
import { computed, defineProps, type PropType, ref, toRef } from 'vue'
import { FavoriteButton } from '@haskoning/vue-component-library'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import router from '@/router'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'

const props = defineProps({
  item: {
    type: Object as PropType<Calculation>,
    required: true
  },
  isCheckboxShown: {
    type: Boolean,
    default: false
  }
})

const item = toRef(props, 'item')
const clickedItem = ref<number | null>(null)

const calculationStore = useCalculationStore()
const { selectedItems, fields, errorLoading } = storeToRefs(calculationStore)

const isSelected = computed<boolean>({
  get: () => selectedItems.value?.includes(item.value) || false,
  set: () => {
    const index = selectedItems.value.findIndex(
      (selectedItem: Calculation) => selectedItem.id === item.value?.id
    )

    index !== -1 ? selectedItems.value.splice(index, 1) : selectedItems.value.push(item.value)
  }
})

const navigateToCalculation = () => {
  const routeName =
    item.value?.status !== STATUS_CHOICES.DRAFT ? 'calculation-result' : 'calculation-form'
  router.push({
    name: routeName,
    params: { id: item.value?.project, calculationId: item.value?.id }
  })
}

const updateFavorite = async () => {
  if (item.value) {
    item.value.favorite = !item.value.favorite
    await calculationStore.update(item.value)
  }
}

const handleChangeCalculation = () => {
  calculationStore.setCurrentItem(item.value)
  calculationStore.openCreateUpdateDialog()
}

const getStatus = (item: Calculation): string => {
  const choices = fields.value['status']?.choices ?? []
  return choices.find((choice) => choice.value === item.status)?.display_name ?? ''
}

const downloadErrorFile = async () => {
  if (item.value?.id) {
    clickedItem.value = item.value.id
    await calculationStore.downloadErrorFile(item.value.id)
    clickedItem.value = null
  }
}
</script>

<template>
  <tr v-ripple style="cursor: pointer" @click.stop="navigateToCalculation">
    <td v-if="isCheckboxShown">
      <v-checkbox v-model="isSelected" density="compact" hide-details @click.stop="" />
    </td>
    <td>{{ item.id }}</td>
    <td>{{ item.name }}</td>
    <td>{{ getStatus(item) }}</td>
    <td class="text-center">{{ item.crosssection_count }}</td>
    <td class="text-center">
      <v-btn
        icon="mdi-file-download-outline "
        density="comfortable"
        variant="text"
        :loading="errorLoading && clickedItem === item.id"
        @click.stop="downloadErrorFile"
      >
      </v-btn>
    </td>
    <td>{{ DateTime.fromISO(item.created).toLocaleString(DateTime.DATETIME_MED) }}</td>
    <td>
      {{
        item.modified ? DateTime.fromISO(item.modified).toLocaleString(DateTime.DATETIME_MED) : '-'
      }}
    </td>
    <td class="text-center">
      <FavoriteButton :item="item" @update-favorite="updateFavorite" />
    </td>
    <td v-if="calculationStore.canChangeCalculation" class="text-center">
      <v-btn variant="text" :icon="'$edit'" @click.stop="handleChangeCalculation" />
    </td>
  </tr>
</template>

<style scoped></style>
